import type { Components, Theme } from "@mui/material/styles";

const focusVisibleStyles = (theme: Theme) => ({
  outline: `2px solid ${theme.palette.primary.main}`,
  outlineOffset: "2px",
  boxShadow: `0 0 0 4px ${theme.palette.semantic.primary.focus}`
});

export const components: Components<Theme> = {
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true
    },
    styleOverrides: {
      root: ({ theme }) => ({
        "&.Mui-focusVisible": focusVisibleStyles(theme),
        "&.MuiButton-text": {
          textTransform: "none",
          textDecoration: "underline",
          "&:hover": {
            textDecoration: "underline"
          },
          "&.Mui-focusVisible": focusVisibleStyles(theme)
        }
      })
    }
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: "2rem",
        padding: "0.625rem 1.25rem"
      }
    },
    variants: [
      {
        props: { size: "small" },
        style: {
          padding: "0.25rem 0.75rem",
          fontSize: "0.75rem"
        }
      },
      {
        props: { variant: "contained" },
        style: ({ theme }) => ({
          "&:hover": {
            backgroundColor: theme.palette.primary.main
          }
        })
      },
      {
        props: { variant: "text" },
        style: {
          "&.MuiButton-text": {
            textDecoration: "none",
            textTransform: "uppercase",
            "&:hover": {
              textDecoration: "none"
            }
          }
        }
      }
    ]
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: theme.typography.h4.fontSize,
        fontWeight: theme.typography.h4.fontWeight,
        lineHeight: theme.typography.h4.lineHeight,
        letterSpacing: theme.typography.h4.letterSpacing,
        padding: theme.spacing(2)
      })
    }
  },
  MuiTextField: {
    styleOverrides: {
      root: ({ theme }) => ({
        "& label.Mui-focused": {
          color: theme.palette.text.primary
        },
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: theme.palette.misc.outlineBorder
          },
          "&:hover fieldset": {
            borderColor: theme.palette.text.primary
          },
          "&.Mui-focused fieldset": {
            borderColor: theme.palette.text.primary
          }
        }
      })
    }
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        // MUI defaults to body1 (16px) and scales to ~12px on shrink. Applying
        // inputLabel (12px) globally made empty-field labels look too small.
        ...theme.typography.body1
      })
    }
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.helperText
      })
    }
  },
  MuiInputBase: {
    styleOverrides: {
      input: ({ theme }) => ({
        ...theme.typography.inputText
      })
    }
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        ...theme.typography.tooltip,
        backgroundColor: theme.palette.misc.snackbarBackground,
        padding: theme.spacing(1, 1.5),
        maxWidth: 320
      }),
      arrow: ({ theme }) => ({
        color: theme.palette.misc.snackbarBackground
      })
    }
  },
  MuiLink: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.link
      })
    }
  },
  MuiAvatar: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.avatarLetter
      })
    }
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        "> .MuiFormLabel-asterisk": {
          color: theme.palette.primary.main,
          fontWeight: theme.typography.fontWeightBold
        }
      })
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        cursor: "pointer"
      }
    }
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        minHeight: "0 !important",
        "&.Mui-expanded": {
          marginTop: 0,
          minHeight: "0 !important"
        },
        ".MuiAccordionSummary-root": {
          minHeight: "0 !important",
          ".MuiAccordionSummary-content": {
            minHeight: "0 !important",
            ".MuiTypography-root": {
              cursor: "pointer"
            },
            "&.Mui-expanded": {
              minHeight: "0 !important",
              marginTop: "12px",
              marginBottom: "12px"
            }
          }
        }
      }
    }
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        ".Mui-disabled": {
          cursor: "not-allowed"
        }
      }
    }
  },
  /**
   * Match `dev` Alert look. MUI defaults to lighten(palette.*.light, 0.9); on
   * `dev`, `.light` was nearly `.main`, but Figma `.light` is pastel so that
   * formula washes out. Use `.main` as the base (same formula, closer to `dev`).
   */
  MuiAlert: {
    styleOverrides: {
      standardSuccess: ({ theme }) => ({
        backgroundColor: theme.lighten(theme.palette.success.main, 0.9),
        color: theme.darken(theme.palette.success.main, 0.6),
        ".MuiAlert-icon": {
          color: theme.palette.success.main
        }
      }),
      standardInfo: ({ theme }) => ({
        backgroundColor: theme.lighten(theme.palette.info.main, 0.9),
        color: theme.darken(theme.palette.info.main, 0.6),
        ".MuiAlert-icon": {
          color: theme.palette.info.main
        }
      }),
      standardWarning: ({ theme }) => ({
        backgroundColor: theme.lighten(theme.palette.warning.main, 0.9),
        color: theme.darken(theme.palette.warning.main, 0.6),
        ".MuiAlert-icon": {
          color: theme.palette.warning.main
        }
      }),
      standardError: ({ theme }) => ({
        backgroundColor: theme.lighten(theme.palette.error.main, 0.9),
        color: theme.darken(theme.palette.error.main, 0.6),
        ".MuiAlert-icon": {
          color: theme.palette.error.main
        }
      })
    }
  }
  // MuiCssBaseline used to live here with Montserrat @font-face rules as a
  // styleOverrides string. Those rules are now in src/styles/App.scss (loaded
  // globally via App.tsx's `import "./styles/App.scss"`), which keeps the
  // exact same fonts at the exact same URLs without routing them through
  // the MUI theme.
};
